/**
 * useLocationUrl — rewrites legacy `/location/<rest>` URLs to the
 * path-scoped shape `/location/{activeLocationId}/<rest>`.
 *
 * Replaces hardcoded `/location/...` link bodies after the scope-in-URL
 * refactor (`docs/plan/SCOPE_IN_URL.md`). Pages that read
 * `useActiveContext` already have the id; call sites just wrap their
 * existing string.
 *
 *   const locUrl = useLocationUrl();
 *   <Link to={locUrl('/location/treasury/operations')}>…</Link>
 *
 * No-ops in three cases (returns the input unchanged):
 *   1. URL doesn't start with `/location/` (e.g. `/company/...`,
 *      `/profile`).
 *   2. URL already includes the active location id as the first
 *      segment after `/location/`.
 *   3. No active location id (company scope or no scope) — left as
 *      legacy so the catch-all in `App.tsx` handles it.
 */

import { useCallback } from 'react';

import { useActiveContext } from './useActiveContext';

export function useLocationUrl(): (url: string) => string {
  const { activeLocationId } = useActiveContext();
  return useCallback(
    (url: string): string => {
      if (!url || !url.startsWith('/location/')) return url;
      if (!activeLocationId) return url;
      const rest = url.slice('/location/'.length);
      const firstSegment = rest.split('/')[0].split('?')[0];
      if (firstSegment === activeLocationId) return url;
      return `/location/${activeLocationId}/${rest}`;
    },
    [activeLocationId],
  );
}
